import React from 'react';
import { Box } from '@mui/material';
import { formatSecToHourString, formatSecToMinString } from 'helpers/conversions';
import { TimeFrame } from 'models/ReportData';
import DesktopAverageProductionRateCardDtg from './DesktopAverageProductionRateCardDtg';
import DesktopProductivityCardDtg from './DesktopProductivityCardDtg';
import DesktopTotalImpressionCardDtg from './DesktopTotalImpressionCard';
import { ITopCardsProps } from './TopCardsContainer';
import DesktopAverageAvailabilityCardDtg from './DesktopAverageAvailabilityCardDtg';
import { desktopDtgApolloTopCardsStyles } from './DesktopDtgApolloTopCards.styles';

const DesktopDtgTopCards = (props: ITopCardsProps) => {
  const {
    totalProduction,
    totalProductionFull,
    units,
    selectedSystemGroup,
    productionTrend,
    selectedSystemGroupText,
    timeframe,
    productionRate,
    productionRateTrend,
    loadingTime,
    loadingTimeTrend,
    darkImpressionsPercentage,
    darkImpressionsTrend,
    productivityChange,
    productivity,
    printingTimeTrend,
    printingTime,

    availabilityTrend,
    availability,

    customTimeframeProperties,
    availabilityTotalDurationReported,
    maintenanceDurationTrend,
    maintenanceDuration,
    errorDurationTrend,
    errorDuration,
    isProductionMode,
  } = props;
  const darkImpressionsValue =
    darkImpressionsPercentage !== undefined
      ? darkImpressionsPercentage * 100
      : darkImpressionsPercentage;
  const getTimeframeTextKey = () => {
    return timeframe && timeframe.includes('lastUpdate') ? 'last' : 'weekDay';
  };
  const changeTextPerPage = (timeframe: TimeFrame | '', text: string) => {
    if (timeframe && timeframe.includes('lastUpdate')) return text.replace('Average', 'Current');
    return text;
  };
  return (
    <Box
      display="flex"
      gap="16px"
      sx={desktopDtgApolloTopCardsStyles.topCardsDesktopWrapper}
      data-testid="top-cards-desktop-wrapper">
      <Box
        display="flex"
        boxSizing="border-box"
        height="144px"
        sx={
          isProductionMode
            ? desktopDtgApolloTopCardsStyles.totalImpressionCardWrapperProductionMode
            : desktopDtgApolloTopCardsStyles.totalImpressionCardWrapper
        }>
        <DesktopTotalImpressionCardDtg
          customTimeframeProperties={customTimeframeProperties}
          //*in Production Hours mode `totalProduction` is the value within production hours, `totalProductionFull` - the general total
          value={totalProduction}
          units={units}
          stats={productionTrend}
          selectedSystemGroup={selectedSystemGroup}
          selectedSystemGroupText={selectedSystemGroupText}
          description={
            isProductionMode
              ? selectedSystemGroupText.impression.withinProductionHoursLabel
              : selectedSystemGroupText.impression.description[getTimeframeTextKey()]
          }
          timeframe={timeframe}
          secondaryMetric={
            isProductionMode
              ? {
                  value: totalProductionFull,
                  unit: units,
                  description: selectedSystemGroupText.impression.totalLabel,
                }
              : undefined
          }
          addedData={{
            upper: {
              unit: '%',
              value: darkImpressionsValue,
              description: selectedSystemGroupText.productionRate.lower,
              trend: darkImpressionsTrend,
            },
          }}
        />
      </Box>
      <Box
        display="flex"
        boxSizing="border-box"
        height="144px"
        sx={
          isProductionMode
            ? desktopDtgApolloTopCardsStyles.averageProductionRateCardWrapperProductionMode
            : desktopDtgApolloTopCardsStyles.averageProductionRateCardWrapper
        }>
        <DesktopAverageProductionRateCardDtg
          isProductionMode={isProductionMode}
          selectedSystemGroup={selectedSystemGroup}
          timeframe={timeframe}
          customTimeframeProperties={customTimeframeProperties}
          value={productionRate}
          units={units + '/h'}
          description={
            isProductionMode
              ? selectedSystemGroupText.productionRate.descriptionProduction
              : selectedSystemGroupText.productionRate.description[getTimeframeTextKey()]
          }
          stats={productionRateTrend}
          statsDescription={
            selectedSystemGroupText.productionRate.statsDescription[getTimeframeTextKey()]
          }
        />
      </Box>
      <Box
        display="flex"
        boxSizing="border-box"
        height="144px"
        sx={
          isProductionMode
            ? desktopDtgApolloTopCardsStyles.productivityCardWrapperProductionMode
            : desktopDtgApolloTopCardsStyles.productivityCardWrapper
        }>
        <DesktopProductivityCardDtg
          selectedSystemGroup={selectedSystemGroup}
          timeframe={timeframe}
          customTimeframeProperties={customTimeframeProperties}
          description={
            isProductionMode
              ? selectedSystemGroupText.utilization?.descriptionProduction
              : 'Productivity'
          }
          units={units + '/h'}
          value={productivity}
          stats={productivityChange}
          statsDescription={
            selectedSystemGroupText.productionRate.statsDescription[getTimeframeTextKey()]
          }
          addedData={{
            upper: {
              unit: 'min',
              value: printingTime,
              description: 'Avg. Print Time',
              trend: printingTimeTrend,
              formatter: formatSecToMinString,
            },
            lower: {
              unit: 'sec/' + units,
              value: loadingTime,
              description: changeTextPerPage(
                timeframe,
                selectedSystemGroupText.productionRate.upper
              ),
              trend: loadingTimeTrend,
            },
          }}
          cardName="productivity"
        />
      </Box>
      <Box
        display="flex"
        boxSizing="border-box"
        height="144px"
        sx={
          isProductionMode
            ? desktopDtgApolloTopCardsStyles.averageAvailabilityCardWrapperProductionMode
            : desktopDtgApolloTopCardsStyles.averageAvailabilityCardWrapper
        }>
        <DesktopAverageAvailabilityCardDtg
          selectedSystemGroup={selectedSystemGroup}
          timeframe={timeframe}
          customTimeframeProperties={customTimeframeProperties}
          cardName="availability"
          value={availability}
          units={'%'}
          isPercentage={true}
          description={
            isProductionMode
              ? selectedSystemGroupText.availability.descriptionProduction
              : selectedSystemGroupText.availability.description[getTimeframeTextKey()]
          }
          stats={availabilityTrend}
          statsDescription={
            selectedSystemGroupText.availability.statsDescription[getTimeframeTextKey()]
          }
          addedData={{
            upper: {
              unit: '',
              value: errorDuration,
              description: selectedSystemGroupText.availability.upper,
              trend: errorDurationTrend,
              formatter: formatSecToHourString,
            },
            lower: {
              value: maintenanceDuration,
              unit: '',
              description: selectedSystemGroupText.availability.lower,
              trend: maintenanceDurationTrend,
              formatter: formatSecToHourString,
            },
          }}
          availabilityTotalDurationReported={availabilityTotalDurationReported}
        />
      </Box>
    </Box>
  );
};
export default DesktopDtgTopCards;
